/**
 * Tells a question that *compares* figures apart from one that asks for a
 * single total.
 *
 * "What was my revenue last month" wants one number. "Which month had my
 * highest revenue", "what months do I tend to earn the most" and "which yard
 * makes the most" all contain the same money word, but answering them with a
 * 12-month total — which is what phrase matching did — is confidently wrong.
 * Total skills use {@link asksForComparison} to decline these, so they go to a
 * skill (or the backend agent) that can actually rank.
 *
 * Deliberately regex-based rather than fuzzy. The deciding words here are short
 * ("most", "top", "best"), and at the slack fuzzy matching allows they sit one
 * edit from unrelated words ("post", "to", "rest"). A missed typo costs a total
 * where a ranking was wanted, which is what happened before; a false match
 * would divert plain total questions, which is worse.
 */

/** What is being compared across. */
export type ComparisonDimension =
  /** Calendar months — the only one answered locally, from the payouts breakdown. */
  | 'month'
  | 'location'
  | 'buyer'
  /** Years, quarters or two named periods set against each other. */
  | 'period';

/** What kind of answer the question wants. */
export type ComparisonShape =
  /** "Which month is usually best" — a pattern across years. */
  | 'seasonal'
  /** "Which month had the most" — the extremes of one window. */
  | 'rank'
  /** "Is my revenue going up" — direction over time. */
  | 'trend'
  /** "Revenue month by month" — every value in order. */
  | 'series';

export interface ComparisonQuestion {
  dimension: ComparisonDimension;
  shape: ComparisonShape;
  /** True when the question asks for the low end — "worst", "slowest", "least". */
  wantsLowest: boolean;
}

const HIGH_WORDS =
  /\b(most|more|highest|higher|best|better|top|biggest|largest|greatest|strongest|peak|busiest|max|maximum)\b/;
const LOW_WORDS =
  /\b(least|less|lowest|lower|worst|worse|smallest|weakest|slowest|quietest|fewest|fewer|min|minimum)\b/;

const SEASONAL_WORDS =
  /\b(tends?\s+to|usually|typically|normally|generally|historically|seasonal|seasonality|seasons?|each\s+year|every\s+year|year\s+after\s+year|time\s+of\s+(the\s+)?year|on\s+average)\b/;

// No "declined": here it means a payment or booking was rejected, not a fall.
const TREND_WORDS =
  /\b(trends?|trending|over\s+time|growing|growth|going\s+(up|down)|increas(e|es|ed|ing)|decreas(e|es|ed|ing)|declin(e|es|ing)|dropp(ed|ing)|improv(ed|ing))\b/;

// "month to month" is deliberately absent: here it names a booking type.
const SERIES_WORDS =
  /\b(month\s*(by|over)\s*month|(per|each|every|by)\s+month|monthly|broken\s+down\s+by\s+month|break\s*down\s+by\s+month)\b/;

/** Per quarter or per year — compared across periods, which only the agent breaks down. */
const PERIOD_SERIES_WORDS =
  /\b(year\s*(by|over)\s*year|quarter\s*(by|over)\s*quarter|(per|each|every|by)\s+(year|quarter)|quarterly|yearly)\b/;

/** Two periods named against each other: "this year vs last year", "more than last year". */
const VERSUS_WORDS = /\b(vs|versus|compared?\s+(to|with)|comparison|than)\b/;

/**
 * Period phrases that mention a month or year without comparing across them.
 * Removed before looking for a dimension, so "the most revenue last month" is
 * not mistaken for "which month had the most revenue".
 */
const PERIOD_PHRASES = [
  /\b(this|last|next|previous|past|prior|current|same)\s+(month|year|quarter|season)\b/g,
  /\b(last|past|previous|prior|next)\s+(\d+|[a-z]+)\s+(months|years|quarters)\b/g,
  /\b(last|past|previous|prior|next)\s+(months|years|quarters)\b/g,
  /\b\d+\s+(months|years|quarters)\b/g,
  /\b(month|year|quarter)[\s-]+to[\s-]+date\b/g,
  /\bmonth[\s-]+to[\s-]+month\b/g,
];

const DIMENSION_NOUNS: Array<[ComparisonDimension, RegExp]> = [
  ['month', /\b(months?|monthly|seasons?|seasonal|seasonality|time\s+of\s+(the\s+)?year)\b/],
  ['location', /\b(locations?|yards?|sites?|facilit(y|ies)|propert(y|ies)|lots?|terminals?)\b/],
  ['buyer', /\b(buyers?|customers?|clients?|compan(y|ies)|tenants?|carriers?|who)\b/],
  ['period', /\b(years?|yearly|annual|annually|quarters?|quarterly)\b/],
];

/** How far past "which/what" to look for the noun being chosen: "which of my yards". */
const WHICH_REACH = 3;

const normalise = (message: string): string =>
  message.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').replace(/\s+/g, ' ').trim();

const stripPeriods = (text: string): string =>
  PERIOD_PHRASES.reduce((result, pattern) => result.replace(pattern, ' '), text);

const dimensionOfNoun = (noun: string): ComparisonDimension | null => {
  for (const [dimension, pattern] of DIMENSION_NOUNS) {
    if (pattern.test(noun)) return dimension;
  }
  return null;
};

/**
 * The dimension a question compares across, preferring the noun after
 * "which/what" — "which yard earns the most per month" is choosing a yard — and
 * otherwise the first dimension mentioned, months first.
 */
const findDimension = (text: string): ComparisonDimension | null => {
  if (/\btime\s+of\s+(the\s+)?year\b/.test(text)) return 'month';

  const words = text.split(/[\s-]+/);
  for (let index = 0; index < words.length; index += 1) {
    if (words[index] !== 'which' && words[index] !== 'what') continue;
    for (const noun of words.slice(index + 1, index + 1 + WHICH_REACH)) {
      const dimension = dimensionOfNoun(noun);
      if (dimension) return dimension;
    }
  }

  for (const [dimension, pattern] of DIMENSION_NOUNS) {
    if (pattern.test(text)) return dimension;
  }

  // "When do I make the most" asks for a time of year.
  return /\bwhen\b/.test(text) ? 'month' : null;
};

/**
 * Reads a message as a comparison, or null when it asks for a single figure.
 *
 * Needs a comparing word (a ranking, a trend, a seasonal pattern or a
 * breakdown) *and* something to compare across. "What is my highest revenue"
 * names no dimension, so it stays a total rather than guessing one.
 */
export const detectComparison = (message: string): ComparisonQuestion | null => {
  const text = normalise(message);
  const series = SERIES_WORDS.test(text);
  const stripped = stripPeriods(text);

  const high = HIGH_WORDS.test(stripped);
  const low = LOW_WORDS.test(stripped);
  const seasonal = SEASONAL_WORDS.test(stripped);
  const trend = TREND_WORDS.test(stripped);
  const versus = VERSUS_WORDS.test(stripped);
  const periodSeries = PERIOD_SERIES_WORDS.test(text);
  const ranks = high || low;

  if (!ranks && !seasonal && !trend && !series && !versus && !periodSeries) return null;

  const wantsLowest = low && !high;
  const named = findDimension(stripped);

  const shape: ComparisonShape = trend ? 'trend' : 'rank';

  // A location or buyer being chosen outranks any period words around it:
  // "which yard earned the most each year" is still choosing a yard.
  if (named === 'location' || named === 'buyer') return { dimension: named, shape, wantsLowest };

  if (named === 'period' || versus || (periodSeries && !series)) {
    return { dimension: 'period', shape, wantsLowest };
  }

  if (seasonal) return { dimension: 'month', shape: 'seasonal', wantsLowest };
  if (named === 'month' && ranks) return { dimension: 'month', shape: 'rank', wantsLowest };
  if (trend) return { dimension: 'month', shape: 'trend', wantsLowest };
  if (series) return { dimension: 'month', shape: 'series', wantsLowest };

  return null;
};

/** True when the message compares figures rather than asking for one total. */
export const asksForComparison = (message: string): boolean => detectComparison(message) !== null;

/**
 * True when the message compares across time — months, years, a trend — rather
 * than across locations or buyers. For skills answering a live figure or one
 * total, which cannot say how it changed over time but may still split by
 * location.
 *
 * Looks past the primary dimension: "which company made the most revenue each
 * month" chooses a company, but per month, which a single ranking cannot give.
 */
export const asksForTimeComparison = (message: string): boolean => {
  const dimension = detectComparison(message)?.dimension;
  if (!dimension) return false;
  if (dimension === 'month' || dimension === 'period') return true;

  const text = normalise(message);
  const stripped = stripPeriods(text);
  return (
    SERIES_WORDS.test(text) ||
    PERIOD_SERIES_WORDS.test(text) ||
    SEASONAL_WORDS.test(stripped) ||
    TREND_WORDS.test(stripped)
  );
};
